// The Pause/Resume decision, framework-free so both the creative detail button
// (which offers the action) and the server capability (which authorizes and
// commits it) reason about staleness the exact same way (issue 15).
//
// The action is a single-field Ad-status edit: Pause sets the ad's own intent to
// PAUSED, Resume to ACTIVE. It is the same durable Edit the Launch workspace
// runs, surfaced from Media Performance — never a separate provider shortcut.
import type { EntityStatusValue } from "./creative-entity";

export type CreativeStatusAction = "pause" | "resume";

// The provider Ad status the durable Edit will request. Mirrors ProviderAdStatus
// on the server without importing server code.
export type CreativeAdStatus = "ACTIVE" | "PAUSED";

// A displayed or freshly-read status pair. Ad status is the ad's own Active or
// Paused intent; Delivery status is the provider-effective ability to deliver
// after parent campaign/ad-group states. Both are the normalized vocabulary in
// creative-entity.ts.
export interface CreativeStatusSnapshot {
  ad: EntityStatusValue | null;
  delivery: EntityStatusValue | null;
}

// Which action a creative can offer from its own current Ad status. Only a clear
// Active or Paused intent is toggleable; in-review, rejected, deleted, unknown,
// or a missing status offer nothing so the surface never invites an unsafe write.
export function statusActionFor(
  adStatus: EntityStatusValue | null | undefined,
): CreativeStatusAction | null {
  if (adStatus === "active") return "pause";
  if (adStatus === "paused") return "resume";
  return null;
}

export function desiredAdStatusFor(action: CreativeStatusAction): CreativeAdStatus {
  return action === "pause" ? "PAUSED" : "ACTIVE";
}

// The Ad status an action expects to find before it runs: Pause expects Active,
// Resume expects Paused. A fresh read that no longer shows this means the ad
// moved since the member saw it, so the action stops rather than overwrites.
function expectedFromAdStatus(action: CreativeStatusAction): EntityStatusValue {
  return action === "pause" ? "active" : "paused";
}

export type CreativeStatusDecision =
  // The fresh read still matches what was shown; commit the durable Edit.
  | { decision: "ready"; desiredStatus: CreativeAdStatus }
  // The ad's own Active/Paused intent changed on the provider since display
  // (someone else paused/resumed it, or it is already in the target state).
  | { decision: "stale_ad" }
  // A parent campaign/ad-group state changed since display, so Delivery status
  // no longer matches — stop rather than act on a stale delivery picture.
  | { decision: "stale_delivery" }
  // The fresh Ad status is not a clean Active/Paused, so no safe toggle exists.
  | { decision: "unsupported" };

// Decide whether a Pause/Resume may proceed. Pure: the caller supplies the fresh
// provider read (never a cached reporting/preview value) and the snapshot the
// member was shown. Ad-status change is checked before delivery so "the ad
// already moved" is reported precisely; delivery is only compared when both the
// displayed and fresh delivery are known so an unknown reading never fabricates
// a stop.
export function evaluateCreativeStatusAction(args: {
  action: CreativeStatusAction;
  displayed: CreativeStatusSnapshot;
  fresh: CreativeStatusSnapshot;
}): CreativeStatusDecision {
  const { action, displayed, fresh } = args;
  const expected = expectedFromAdStatus(action);

  if (fresh.ad !== "active" && fresh.ad !== "paused") {
    return { decision: "unsupported" };
  }
  if (fresh.ad !== expected) {
    // The ad is no longer in the state the action assumed — it was changed on
    // the provider (or is already in the target state). Stop safely.
    return { decision: "stale_ad" };
  }
  if (
    displayed.delivery != null &&
    displayed.delivery !== "unknown" &&
    fresh.delivery != null &&
    fresh.delivery !== "unknown" &&
    fresh.delivery !== displayed.delivery
  ) {
    return { decision: "stale_delivery" };
  }
  return { decision: "ready", desiredStatus: desiredAdStatusFor(action) };
}
